import { camelCase, isObject } from 'lodash-es';
import { useCallback, useMemo, type ReactNode } from 'react';
import { useAggregationsContext } from '../../contexts/AggregationsContext';
import { useStrategiesContext } from '../../contexts/StrategiesContext';
import { DEFAULT_AGGREGATION_DISPLAY_NAME } from '../../tokens';
import { ParameterTypeEnum } from '../../types/OrderStrategy.types';
import { PresenceEnum, type IStrategyParameterEnum } from '../../types/types';
import {
  EMPTY_ARRAY,
  format,
  formattedDate,
  includesEnumValue,
  prettyName,
  prettyPriceProtection,
  prettyTimeUnit,
  toBig,
} from '../../utils';
import {
  canParseAsDuration,
  DateTimeDurationPickerValueType,
  formatDuration,
  isDateTimeDurationPickerValue,
} from '../DateTimeDurationPicker';
import { Text } from '../Text';

/**
 * Normalized strategy metadata used by parameter-row consumers.
 */
export interface ParameterRowMetadata {
  raw?: unknown;
  section?: string;
  unitsParam?: string;
}

export interface ParameterRow {
  name?: string;
  title: string;
  value: ReactNode;
  metadata?: ParameterRowMetadata;
  parameterType?: string;
  isEnabled?: boolean;
  suffix?: string;
  strategyIndex?: number;
}

/**
 * Strategy-parameter definition shape used by row transformation callbacks.
 */
export interface ParameterRowDefinition {
  Name: string;
  DisplayName?: string;
  Type?: string;
  Presence?: string;
  DefaultValue?: string;
  EnumValues?: IStrategyParameterEnum[];
  Metadata?: unknown;
}

/**
 * Lookup entry for quick by-name access while transforming rows.
 */
export interface ParameterRowLookupItem {
  definition: ParameterRowDefinition;
  rawValue: unknown;
  metadata?: ParameterRowMetadata;
}

/**
 * Payload passed into optional `rowTransform` callbacks.
 */
export interface ParameterRowTransformInput {
  row: ParameterRow;
  definition: ParameterRowDefinition;
  rawValue: unknown;
  strategy: {
    Parameters: ParameterRowDefinition[];
  };
  orderParameters?: { [key: string]: any };
  rowsByName: Map<string, ParameterRowLookupItem>;
}

/**
 * Optional behavior overrides for `useParameterRows`.
 */
export interface UseParameterRowsOptions {
  rowTransform?: (input: ParameterRowTransformInput) => ParameterRow | null | undefined;
  orderEndTime?: string;
}

const TIME_PARAMETER_TYPES: ParameterTypeEnum[] = [
  ParameterTypeEnum.Date,
  ParameterTypeEnum.Duration,
  ParameterTypeEnum.Interval,
];
const ENABLED_STATE_PARAMETER_TYPES = new Set<string>([
  ParameterTypeEnum.Mode,
  ParameterTypeEnum.PriceProtection,
  ParameterTypeEnum.UnifiedLiquidity,
  ParameterTypeEnum.ReduceOnly,
  ParameterTypeEnum.ReduceFirst,
]);

/**
 * Builds display rows for strategy parameters using baseline formatting behavior.
 * Consumers can optionally inject row-level transformations without affecting
 * default rendering in callers that do not use transforms.
 */
export const useParameterRows = (
  orderStrategy?: string,
  orderParameters?: { [key: string]: any },
  showEmptyParams = false,
  excludedParams: string[] = EMPTY_ARRAY,
  options?: UseParameterRowsOptions
): ParameterRow[] => {
  const { strategiesByName } = useStrategiesContext();

  const { aggregationsByName } = useAggregationsContext();

  const getAggregationDisplayNameByName = useCallback(
    (name: string) => {
      if (name == null || name === '') {
        return DEFAULT_AGGREGATION_DISPLAY_NAME;
      } else {
        return aggregationsByName?.get(name)?.DisplayName;
      }
    },
    [aggregationsByName]
  );

  const rows = useMemo(() => {
    if (!orderStrategy || !strategiesByName) {
      return [];
    }

    const strategy = strategiesByName.get(orderStrategy);
    if (!strategy) {
      return [];
    }

    const parameters = strategy.Parameters as ParameterRowDefinition[];
    const orderEndTime = options?.orderEndTime;
    const hasAbsoluteEndTime =
      hasNonEmptyParamValue(getParamFromOrderParams(orderParameters ?? {}, 'EndTime')) ||
      hasNonEmptyParamValue(orderEndTime);
    const rowsByName = new Map<string, ParameterRowLookupItem>();
    for (const parameter of parameters) {
      rowsByName.set(parameter.Name, {
        definition: parameter,
        rawValue: resolveEndTimeRawValue(
          parameter.Name,
          getParamFromOrderParams(orderParameters ?? {}, parameter.Name),
          orderEndTime
        ),
        // Normalize once so rowTransform callbacks read consistent metadata fields.
        metadata: normalizeParameterMetadata(parameter.Metadata),
      });
    }

    const compactedRows = parameters
      .map((parameter, strategyIndex) => {
        if (orderParameters == null) {
          return null;
        }
        if (excludedParams.includes(parameter.Type ?? '')) {
          return null;
        }
        if (shouldSuppressHiddenDurationRow(parameter, hasAbsoluteEndTime)) {
          return null;
        }
        const name = parameter.Name;
        const title = parameter.DisplayName ?? name;
        const rawValue = rowsByName.get(name)?.rawValue;
        const metadata = rowsByName.get(name)?.metadata;
        let value: any = rawValue;
        if (isObject(value) && !isDateTimeDurationPickerValue(value)) {
          return null;
        }
        if (parameter.Type === ParameterTypeEnum.Date) {
          if (isDateTimeDurationPickerValue(value)) {
            value =
              value.type === DateTimeDurationPickerValueType.Duration
                ? formatDuration(value.value)
                : formattedDate(value.value);
          } else {
            value = canParseAsDuration(value) ? value : formattedDate(value);
          }
        } else if (parameter.Type === ParameterTypeEnum.Aggregation) {
          value = prettyName(getAggregationDisplayNameByName(value));
        } else if (parameter.Type === ParameterTypeEnum.String) {
          value = prettyName(value);
        } else if (parameter.Type === ParameterTypeEnum.Percent) {
          const valueBig = toBig(value);
          value = valueBig ? `${valueBig.times(100).toFixed()}%` : undefined;
        } else if (parameter.Type === ParameterTypeEnum.Enum) {
          // Prettify enum with its Name. EnumValues can be undefined if the strategy
          // subscription delivers an incomplete update; fall back to the raw value.
          const enumValues = (parameter.EnumValues ?? []) as IStrategyParameterEnum[];
          value = enumValues.find(enumValue => value === enumValue.Index)?.Name ?? value;
        } else if (parameter.Type === ParameterTypeEnum.PriceProtection) {
          value = prettyPriceProtection(value);
        } else if (parameter.Name === 'MaxImbalanceAmt') {
          // Keep inline currency formatting while allowing payload-provided companion values.
          const payloadCurrency =
            getParamFromOrderParams(orderParameters, 'MaxImbalanceAmtCurrency') ??
            getParamFromOrderParams(orderParameters, 'MaxImbalanceAmtCurrencyID');
          const strategyDefaultCurrency = strategy.Parameters.find(
            p => p.Name === 'MaxImbalanceAmtCurrency' || p.Name === 'MaxImbalanceAmtCurrencyID'
          )?.DefaultValue;
          const resolvedCurrency = payloadCurrency ?? strategyDefaultCurrency ?? '';
          value = `${format(value, { pretty: true, removeTrailingZeros: true })} ${resolvedCurrency}`.trim();
        } else if (
          parameter.Type &&
          includesEnumValue(
            [ParameterTypeEnum.Price, ParameterTypeEnum.PriceOffset, ParameterTypeEnum.Qty] as const,
            parameter.Type
          )
        ) {
          value = format(value, { pretty: true, removeTrailingZeros: true });
        } else if (parameter.Type === ParameterTypeEnum.Interval) {
          const unit = (value ?? '').at(-1);
          const number = (value ?? '').slice(0, -1);
          value = `${number} ${prettyTimeUnit(unit)}`;
        }

        const isEnabled = resolveEnabledState(value, parameter.Type);

        // Build a baseline row from normalized value, or emit explicit "None" only when empty rows are requested.
        let row: ParameterRow | null = null;
        if (value !== null && value !== undefined && value !== '') {
          row = { name, title, value, metadata, parameterType: parameter.Type, strategyIndex, isEnabled };
        } else if (showEmptyParams) {
          value = <Text color="colorTextSubtle">None</Text>;
          row = { name, title, value, metadata, parameterType: parameter.Type, strategyIndex };
        } else {
          return null;
        }

        if (!row) {
          return null;
        }

        // Let consumers override/augment row output with full parameter context; returning null drops the row.
        if (options?.rowTransform) {
          row =
            options.rowTransform({
              row,
              definition: parameter,
              rawValue,
              strategy: { Parameters: parameters },
              orderParameters,
              rowsByName,
            }) ?? null;
        }

        if (!row) {
          return null;
        }

        // Re-apply empty-value filtering after transformation since callbacks may clear or replace `row.value`.
        if (!showEmptyParams && (row.value == null || row.value === '')) {
          return null;
        }

        return row;
      })
      .compact();

    return [...compactedRows].toSorted((a, b) => {
      const paramA = parameters.find(p => p.Name === a.name);
      const paramB = parameters.find(p => p.Name === b.name);
      const aIsTime = Boolean(paramA?.Type && includesEnumValue(TIME_PARAMETER_TYPES, paramA.Type));
      const bIsTime = Boolean(paramB?.Type && includesEnumValue(TIME_PARAMETER_TYPES, paramB.Type));
      if (aIsTime && !bIsTime) {
        return -1;
      }
      if (!aIsTime && bIsTime) {
        return 1;
      }
      return 0;
    });
  }, [
    orderStrategy,
    strategiesByName,
    orderParameters,
    excludedParams,
    showEmptyParams,
    options,
    getAggregationDisplayNameByName,
  ]);

  return rows;
};

/** Section name for parameters without Metadata.Section (catch-all group). */
export const DEFAULT_STRATEGY_PARAM_SECTION = 'Strategy Parameters';

export interface ParameterRowsGroupedBySection {
  sectionRows: Record<string, ParameterRow[]>;
  sectionNames: string[];
}

/**
 * Returns parameter rows grouped by section. Parameters without Metadata.Section go under DEFAULT_STRATEGY_PARAM_SECTION.
 * Section rows are keyed by section name. Used by ParametersTable for sectioned display.
 */
export function useParameterRowsGroupedBySection(
  orderStrategy?: string,
  orderParameters?: { [key: string]: any },
  showEmptyParams = false,
  excludedParams: string[] = EMPTY_ARRAY,
  orderEndTime?: string
): ParameterRowsGroupedBySection {
  const { strategiesByName } = useStrategiesContext();
  const strategySectionNames = useStrategyParameterSections(orderStrategy);
  const rowOptions = useMemo(() => ({ orderEndTime }), [orderEndTime]);
  const allRows = useParameterRows(orderStrategy, orderParameters, showEmptyParams, excludedParams, rowOptions);

  const { sectionRows, sectionNames } = useMemo(() => {
    const sections: Record<string, ParameterRow[]> = {};

    if (strategySectionNames.length === 0) {
      sections[DEFAULT_STRATEGY_PARAM_SECTION] = allRows;
      return {
        sectionRows: sections,
        sectionNames: allRows.length > 0 ? [DEFAULT_STRATEGY_PARAM_SECTION] : [],
      };
    }

    const strategy = orderStrategy ? strategiesByName?.get(orderStrategy) : undefined;
    if (!strategy) {
      sections[DEFAULT_STRATEGY_PARAM_SECTION] = allRows;
      return {
        sectionRows: sections,
        sectionNames: allRows.length > 0 ? [DEFAULT_STRATEGY_PARAM_SECTION] : [],
      };
    }

    const defaultRows: ParameterRow[] = [];
    for (const row of allRows) {
      const param = strategy.Parameters.find(p => p.Name === row.name);
      if (param?.section) {
        if (!sections[param.section]) {
          sections[param.section] = [];
        }
        sections[param.section].push(row);
      } else {
        defaultRows.push(row);
      }
    }
    if (defaultRows.length > 0) {
      sections[DEFAULT_STRATEGY_PARAM_SECTION] = defaultRows;
    }

    const names =
      defaultRows.length > 0 ? [DEFAULT_STRATEGY_PARAM_SECTION, ...strategySectionNames] : strategySectionNames;

    return { sectionRows: sections, sectionNames: names };
  }, [orderStrategy, strategiesByName, strategySectionNames, allRows]);

  return {
    sectionRows,
    sectionNames,
  };
}

/**
 * Returns the unique section names from strategy parameters that have Metadata.Section set.
 * Used to render separate parameter sections in Order Details.
 */
export function useStrategyParameterSections(orderStrategy?: string): string[] {
  const { strategiesByName } = useStrategiesContext();

  return useMemo(() => {
    if (!orderStrategy || !strategiesByName) {
      return [];
    }
    const strategy = strategiesByName.get(orderStrategy);
    if (!strategy) {
      return [];
    }
    const sections = new Set<string>();
    strategy.Parameters.forEach(param => {
      if (param.section) {
        sections.add(param.section);
      }
    });
    return Array.from(sections);
  }, [orderStrategy, strategiesByName]);
}

function hasNonEmptyParamValue(value: unknown): boolean {
  return value != null && value !== '';
}

function resolveEndTimeRawValue(parameterName: string, rawValue: unknown, orderEndTime?: string): unknown {
  if (parameterName !== 'EndTime' || hasNonEmptyParamValue(rawValue) || !hasNonEmptyParamValue(orderEndTime)) {
    return rawValue;
  }
  return orderEndTime;
}

function shouldSuppressHiddenDurationRow(parameter: ParameterRowDefinition, hasAbsoluteEndTime: boolean): boolean {
  return hasAbsoluteEndTime && parameter.Name === 'Duration' && parameter.Presence === PresenceEnum.Hidden;
}

/**
 * Just solves the case where we might receive both lowercased and uppercased parameters within the order (if we're getting older implementations
 * using the Data model)
 * @param orderParams
 * @param paramName
 * @returns either the parameter value, or undefined otherwise.
 */
function getParamFromOrderParams(orderParams: { [key: string]: any }, paramName: string): any | undefined {
  let param = orderParams[paramName];
  if (param === undefined) {
    // try again with lowercased paramName
    param = orderParams[camelCase(paramName)];
  }

  return param ?? undefined;
}

/**
 * Normalizes strategy parameter metadata from object or JSON-string payloads.
 * Preserves the original `raw` metadata and extracts known string fields
 * (`section`, `unitsParam`) when present.
 * Returns `undefined` only when metadata is missing (`null`/`undefined`).
 * For unsupported or unparseable payloads, returns an object with `raw`
 * preserved and extracted fields left as `undefined`.
 */
function normalizeParameterMetadata(metadata: unknown): ParameterRowMetadata | undefined {
  if (metadata == null) {
    return undefined;
  }

  const parsedMetadata = parseMetadataObject(metadata);
  const metadataRecord = parsedMetadata ?? undefined;

  return {
    raw: metadata,
    section: getStringField(metadataRecord, 'Section'),
    unitsParam: getStringField(metadataRecord, 'UnitsParam'),
  };
}

/**
 * Parses metadata into an object using tolerant rules:
 * - returns object inputs as-is
 * - parses non-empty JSON strings
 * - returns `undefined` for unsupported/invalid values
 * Never throws; invalid JSON is treated as unavailable metadata.
 */
function parseMetadataObject(metadata: unknown): Record<string, unknown> | undefined {
  if (metadata && typeof metadata === 'object') {
    return metadata as Record<string, unknown>;
  }

  if (typeof metadata !== 'string') {
    return undefined;
  }

  const trimmed = metadata.trim();
  if (!trimmed) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(trimmed);
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Reads a trimmed, non-empty string field from metadata.
 * Tries exact key match first, then a case-insensitive fallback key lookup.
 * Returns `undefined` when the key is absent or the value is empty/non-string.
 */
function getStringField(metadata: Record<string, unknown> | undefined, key: string): string | undefined {
  if (!metadata) {
    return undefined;
  }

  const directValue = metadata[key];
  if (typeof directValue === 'string' && directValue.trim()) {
    return directValue.trim();
  }

  const lowerKey = key.toLowerCase();
  const matchingKey = Object.keys(metadata).find(existingKey => existingKey.toLowerCase() === lowerKey);
  const fallbackValue = matchingKey ? metadata[matchingKey] : undefined;
  return typeof fallbackValue === 'string' && fallbackValue.trim() ? fallbackValue.trim() : undefined;
}

function resolveEnabledState(value: unknown, parameterType?: string): boolean | undefined {
  if (!parameterType || !ENABLED_STATE_PARAMETER_TYPES.has(parameterType)) {
    return undefined;
  }

  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'number') {
    if (value === 1) {
      return true;
    }
    if (value === 0) {
      return false;
    }
    return undefined;
  }

  if (typeof value !== 'string') {
    return undefined;
  }

  const normalizedValue = value.trim().toLowerCase();
  if (normalizedValue === 'enabled') {
    return true;
  }
  if (normalizedValue === 'disabled') {
    return false;
  }
  return undefined;
}
